import qs from 'qs';
import errorHandler from 'errorHandler';
import { getI18nMap } from 'utils/i18n';
import { getWoosmapComponents } from 'utils/autocompleteCountries';
import fuzzball from 'fuzzball';

const { REACT_APP_WOOSMAP_AUTOCOMPLETE_API_URL, REACT_APP_WOOSMAP_ADDRESS_API_URL } = process.env;

/**
 @see https://developers.woosmap.com/products/localities/search-city-postcode/
*/

export const getLocalitiesAutocomplete = (input, apiKey, languageCode, minRatio = 75) => {
  if (!REACT_APP_WOOSMAP_AUTOCOMPLETE_API_URL)
    return errorHandler(
      'Be sure you have set "REACT_APP_WOOSMAP_AUTOCOMPLETE_API_URL" on env files',
    );
  if (!apiKey) return errorHandler('"apiKey" param is missing');
  const i18nMap = getI18nMap(languageCode);
  const components = getWoosmapComponents(i18nMap);

  const config = qs.stringify({
    input,
    key: apiKey,
    components,
    language: i18nMap.ISO639,
    types: 'postal_code|locality',
    data: 'advanced',
  });

  return fetch(`${REACT_APP_WOOSMAP_AUTOCOMPLETE_API_URL}?${config}`)
    .then((res) => res.json())
    .then((results) => {
      const localities = results?.localities || [];
      return localities
        .map((locality) => ({
          ...locality,
          ratio: fuzzball.partial_ratio(input, locality.description),
        }))
        .filter(({ ratio }) => ratio >= minRatio);
    })
    .catch((err) => errorHandler(err));
};

export const getAddressAutocomplete = (input, apiKey, languageCode, minRatio = 75) => {
  if (!REACT_APP_WOOSMAP_ADDRESS_API_URL)
    return errorHandler('Be sure you have set "REACT_APP_WOOSMAP_ADDRESS_API_URL" on env files');
  if (!apiKey) return errorHandler('"apiKey" param is missing');

  const i18nMap = getI18nMap(languageCode);
  const components = getWoosmapComponents(i18nMap);

  const config = qs.stringify({
    input,
    key: apiKey,
    components,
    language: i18nMap.ISO639,
  });

  return fetch(`${REACT_APP_WOOSMAP_ADDRESS_API_URL}/autocomplete/json?${config}`)
    .then((res) => res.json())
    .then((results) => {
      const addresses = results?.predictions || [];
      return addresses
        .map((prediction) => ({
          ...prediction,
          ratio: fuzzball.partial_ratio(input, prediction.description),
        }))
        .filter(({ ratio }) => ratio >= minRatio);
    })
    .catch((err) => errorHandler(err));
};

export const getAddressGeocode = (address, apiKey, languageCode) => {
  if (!REACT_APP_WOOSMAP_ADDRESS_API_URL)
    return errorHandler('Be sure you have set "REACT_APP_WOOSMAP_ADDRESS_API_URL" on env files');
  if (!apiKey) return errorHandler('"apiKey" param is missing');

  const i18nMap = getI18nMap(languageCode);
  const components = getWoosmapComponents(i18nMap);

  const config = qs.stringify({
    address,
    key: apiKey,
    components,
    language: i18nMap.ISO639,
  });

  return fetch(`${REACT_APP_WOOSMAP_ADDRESS_API_URL}/geocode/json?${config}`)
    .then((res) => res.json())
    .then((results) => results?.results[0] || [])
    .catch((err) => errorHandler(err));
};

export const getAutocompletePredictions = (input, apiKey, languageCode, minRatio) => {
  const getLocalities = getLocalitiesAutocomplete(input, apiKey, languageCode, minRatio);

  return getLocalities
    .then((localities) =>
      localities.length > 0
        ? localities
        : getAddressAutocomplete(input, apiKey, languageCode, minRatio),
    )
    .catch((err) => errorHandler(err));
};

export const initWoosmap = (apiKey, languageCode, minRatio = 75) => ({
  // eslint-disable-next-line max-len
  getWoosmapAutocompletePredictions: (input) =>
    getAutocompletePredictions(input, apiKey, languageCode, minRatio),
  getWoosmapAddressGeocode: (address) => getAddressGeocode(address, apiKey, languageCode),
});
